import { Component, Input, OnChanges, inject, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { DataPlatformContactRow, DataPlatformContactDetail } from '../../data-platform/data-platform.model';
import { DataPlatformService } from '../../data-platform/data-platform.service';
import { DateService } from '../../shared/services/date-service';
import { SettingsService } from '../../shared/services/admin-settings.service';
import { identitySourceBadge, IdentitySourceBadge } from '../../data-platform/data-platform.utils';
import { FormatDate } from '../../shared/pipes/format-date.pipe';
import { TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

@Component({
  selector: 'app-data-platform-contact-flyout',
  templateUrl: './data-platform-contact-flyout.component.html',
  styleUrls: ['./data-platform-contact-flyout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DataPlatformContactFlyoutComponent implements OnChanges {
  @Input() flyoutOpeningData: FlyoutOpeningData;

  protected readonly contactRow = signal<DataPlatformContactRow | undefined>(undefined);
  protected readonly detail = signal<DataPlatformContactDetail | null>(null);
  protected readonly loading = signal(false);

  protected readonly displayName = computed(() => {
    const detail = this.detail();
    if (detail) {
      const parts = [detail.firstName, detail.lastName].filter(Boolean);
      return parts.length ? parts.join(' ') : (detail.email ?? `#${detail.contactId}`);
    }
    const row = this.contactRow();
    if (row) {
      const parts = [row.firstName, row.lastName].filter(Boolean);
      return parts.length ? parts.join(' ') : (row.email ?? `#${row.contactId}`);
    }
    return '';
  });

  protected readonly traitEntries = computed(() => Object.entries(this.detail()?.properties ?? {}));
  protected readonly consentEntries = computed(() => Object.entries(this.detail()?.consents ?? {}));

  /** From the detail: profiles-list rows carry no consent flag. Falls back to the row when it has one. */
  protected readonly hasEmailConsent = computed(
    () => this.detail()?.consents?.['email'] ?? this.contactRow()?.hasEmailConsent ?? false,
  );

  protected readonly subscriptionsView = computed(() =>
    (this.detail()?.subscriptions ?? []).map((sub) => ({
      ...sub,
      subscribedAtDate: sub.subscribedAt ? this.dateService.utcToLocalTime(sub.subscribedAt) : null,
    })),
  );

  protected readonly consentLogView = computed(() =>
    (this.detail()?.consentLog ?? []).map((entry) => ({
      ...entry,
      recordedAtDate: this.dateService.utcToLocalTime(entry.recordedAt),
    })),
  );

  private readonly dataPlatformService = inject(DataPlatformService);
  private readonly flyoutService = inject(FlyoutService);
  private readonly dateService = inject(DateService);
  private readonly settingsService = inject(SettingsService);
  private readonly translateService = inject(TranslateService);
  private readonly formatDate = inject(FormatDate);

  private readonly adminToggle = toSignal(
    this.settingsService.settings$.pipe(map((settings) => settings.adminToggle)),
    { initialValue: false },
  );

  protected readonly identifiersView = computed(() =>
    (this.detail()?.identifiers ?? []).map((id) => {
      const badge = this.visibleBadge(id.source);
      return { ...id, badge, sourceTooltip: this.sourceTooltip(badge, this.datedOrNull(id.firstSeen)) };
    }),
  );

  /** Imported identifiers carry no date and arrive as the epoch sentinel. */
  private datedOrNull(firstSeen: string | null): Date | null {
    if (!firstSeen) {
      return null;
    }
    const date = this.dateService.utcToLocalTime(firstSeen);
    return date.getFullYear() > 1970 ? date : null;
  }

  /** The source badge for an identifier, hidden when it is admin-only and the viewer is not an admin. */
  private visibleBadge(source: string | null): IdentitySourceBadge | null {
    const badge = identitySourceBadge(source);
    return badge && (!badge.adminOnly || this.adminToggle()) ? badge : null;
  }

  private sourceTooltip(badge: IdentitySourceBadge | null, firstSeen: Date | null): string {
    const trigger = badge?.triggerKey ? this.translateService.instant(badge.triggerKey) : null;
    const seen = firstSeen
      ? this.translateService.instant('data-platform.identifier-first-seen', {
          date: this.formatDate.transform(firstSeen),
        })
      : null;
    return [trigger, seen].filter(Boolean).join(' · ');
  }

  ngOnChanges() {
    const row = this.flyoutOpeningData?.dataPlatformContactRow;
    if (!row) return;
    this.contactRow.set(row);
    this.detail.set(null);
    this.loading.set(true);
    this.dataPlatformService.getContactDetail({ contactId: row.contactId }, 'contacts-flyout-detail').subscribe({
      next: (result) => {
        this.loading.set(false);
        if (result?.isPositive() && result.data) {
          this.detail.set(result.data);
        }
      },
      error: () => this.loading.set(false),
    });
  }

  protected closeFlyout() {
    this.flyoutService.close();
  }
}
